import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import cn from 'classnames';
import styled, { keyframes } from 'styled-components';

import { useWebcamRecorder } from '@/entities/interwills/hooks/useWebcamRecorder';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';
import { dates } from '@/shared/utils/dates';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';

import { RecordingVideo } from '../index';

import { ClosableAlert } from './ClosableAlert';
const THREE_MIN = 3 * 60;

type VideoRecorderProps = {
  setVideo: (obj: RecordingVideo) => void;
};

export const VideoRecorder: FC<VideoRecorderProps> = ({ setVideo }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'recordingModal' });
  const modals = useModalsStore();

  const [countdown, setCountdown] = useState<number | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [isFinish, setIsFinish] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [startedAt, setStartedAt] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const actionAfterCountdown = useRef<(() => void) | null>(null);

  const {
    recordingStatus,
    recordedVideoURL,
    recordedVideoBlob,
    frameRate,
    resolution,
    error,
    timer,
    isReady,
    startRecording,
    stopRecording,
    resumeRecording,
    pauseRecording,
    startCamera,
  } = useWebcamRecorder(videoRef);

  useEffect(() => {
    startCamera();
  }, []);

  useEffect(() => {
    if (recordingStatus !== 'idle') return undefined;

    const tooltipTimer = setTimeout(() => {
      setShowTooltip(true);
    }, 2000);

    return () => {
      clearTimeout(tooltipTimer);
    };
  }, [recordingStatus]);

  useEffect(() => {
    if (error) {
      modals.closeModal();
      modals.openModal({
        type: 'AccessErrorModal',
        onSuccess: startCamera,
      });
    }
  }, [error]);

  const timeLeft = useMemo(() => {
    return Math.floor(THREE_MIN - timer);
  }, [timer]);

  const progress = timer > 0 ? timer / THREE_MIN : 0;

  const onStartRecord = () => {
    if (!isReady) return;
    actionAfterCountdown.current = () => {
      setStartedAt(new Date().toISOString());
      startRecording();
    };
    setCountdown(3);
  };

  const onFinish = () => {
    stopRecording();
    setIsFinish(true);
  };

  const onResume = () => {
    actionAfterCountdown.current = resumeRecording;
    setCountdown(3);
  };

  useEffect(() => {
    if (timeLeft === 30) {
      setShowAlert(true);
    }
    if (timeLeft <= 0) {
      stopRecording();
    }
  }, [timeLeft]);

  useEffect(() => {
    if (countdown === 0) {
      actionAfterCountdown.current?.();
      actionAfterCountdown.current = null;
      setCountdown(null);
      setShowAlert(false);
      return undefined;
    }

    if (countdown === null) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setCountdown((prevCount) => (prevCount ? prevCount - 1 : null));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [countdown]);

  useEffect(() => {
    if (recordedVideoURL && recordedVideoBlob && recordingStatus === 'stopped' && isFinish) {
      setVideo({
        url: recordedVideoURL,
        blob: recordedVideoBlob,
        duration: timer,
        frameRate,
        resolution,
        startedAt,
      });
    }
  }, [recordingStatus, recordedVideoURL, isFinish]);

  const Progress = () => (
    <ProgressCircle
      style={{
        background: `
            conic-gradient(
              from -90deg,
              ${COLORS.systemFlame200} 0deg,
              ${COLORS.systemFlame200} ${progress * 360}deg,
              transparent ${progress * 360}deg,
              transparent 360deg
            )`,
      }}
    />
  );

  return (
    <Container
      onClick={() => {
        setShowTooltip(false);
      }}
    >
      <Time>
        <RedDot className={cn({ blink: recordingStatus === 'recording' })} />{' '}
        <span>{dates.formatSecondsToMmSs(timeLeft)}</span>
      </Time>

      {showAlert && (
        <ClosableAlertStyled onClose={() => setShowAlert(false)} color="red">
          {t('endWarning')}
        </ClosableAlertStyled>
      )}

      <VideoStyled autoPlay ref={videoRef} muted={true} />

      {recordingStatus === 'recording' && (
        <StopRecordButton onClick={pauseRecording}>
          <Progress />
        </StopRecordButton>
      )}

      {recordingStatus === 'idle' && (
        <Tooltip
          open={showTooltip}
          color={COLORS.white}
          overlayInnerStyle={{
            color: COLORS.systemDark300,
          }}
          title={t('recordingHint')}
        >
          <StartRecordButton
            onMouseEnter={() => {
              setShowTooltip(false);
            }}
            onClick={onStartRecord}
          />
        </Tooltip>
      )}
      {(recordingStatus === 'paused' || recordingStatus === 'stopped') && (
        <RecordedControlButtons>
          <ControlButton onClick={onStartRecord}>
            <Icon name="arrow-clockwise" size="s20" />
            <span>{t('retakeText')}</span>
          </ControlButton>
          {recordingStatus === 'paused' ? (
            <ContinueRecordButton onClick={onResume}>
              <Icon name="PlayFilledSmooth" />
              <Progress />
            </ContinueRecordButton>
          ) : (
            <StartRecordButton className="static">
              <Progress />
            </StartRecordButton>
          )}
          <ControlButton onClick={onFinish}>
            <Icon name="check" size="s20" />
            <span>{t('previewText')}</span>
          </ControlButton>
        </RecordedControlButtons>
      )}

      {countdown && <Countdown>{countdown}</Countdown>}
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  position: relative;
  border-radius: 16px;
  overflow: hidden;

  ${media.iwMobileOrLandscape} {
    position: absolute;
    inset: 0;
    border-radius: 0;

    &::after {
      content: '';
      position: absolute;
      background: linear-gradient(1deg, rgba(62, 62, 62, 0) 46.2%, #272727 101.83%);
      inset: 0;
    }
  }
`;

const Time = styled.div`
  padding: 4px 8px;
  display: flex;
  gap: 4px;
  border-radius: 6px;
  background: ${COLORS.white};
  width: fit-content;
  font-size: 10px;
  font-weight: 700;
  align-items: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 20px;

  ${media.iwMobileOrLandscape} {
    top: 112px;
    z-index: 2;
  }

  ${media.iwMobileLandscape} {
    top: 52px;
    right: 16px;
    left: unset;
    transform: unset;
  }
`;

const blinkAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.2; }
`;

const RedDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${COLORS.systemFlame200};

  &.blink {
    animation: ${blinkAnimation} 2s infinite ease-in-out;
  }
`;

const RecordButton = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: 1px solid ${COLORS.systemLight100};
  background: ${COLORS.colorIwRecordButton};
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
  backdrop-filter: blur(7px);
  z-index: 1;
  flex-shrink: 0;
`;

const StartRecordButton = styled(RecordButton)`
  &.static {
    position: static;
    transform: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &::before {
    content: '';
    position: absolute;
    width: 54px;
    height: 54px;
    border-radius: 50%;
    background: ${COLORS.systemFlame200};
    inset: 8px;
  }

  ${media.iwMobileLandscape} {
    order: 1;
  }
`;

const StopRecordButton = styled(RecordButton)`
  &::before {
    content: '';
    position: absolute;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    background: ${COLORS.systemFlame200};
    inset: 24px;
    z-index: 2;
  }
`;

const ProgressCircle = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  z-index: 1;
  mask: radial-gradient(transparent 31px, black 32px);
  transform: rotate(90deg);
`;

const VideoStyled = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Countdown = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: ${COLORS.systemLight50};
  color: ${COLORS.white};
  font-size: 100px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);

  ${media.iwMobileOrLandscape} {
    background: none;
    transform: translateY(-100%) translateX(-50%);
    z-index: 1;
  }
`;

const RecordedControlButtons = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;

  ${media.iwMobile} {
    left: 16px;
    width: calc(100% - 32px);
    transform: unset;
    justify-content: space-between;
  }

  ${media.iwMobileLandscape} {
    left: calc(50% - 36px);
    transform: unset;
    flex-direction: row-reverse;
  }
`;

const ContinueRecordButton = styled(RecordButton)`
  position: static;
  transform: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.systemFlame200};

  ${media.iwMobileLandscape} {
    order: 1;
  }
`;

const ControlButton = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  color: ${COLORS.white};
  font-weight: 700;
  cursor: pointer;
`;

const ClosableAlertStyled = styled(ClosableAlert)`
  position: absolute;
  top: 54px;
  left: 50%;
  transform: translateX(-50%);
  max-width: 375px;
  z-index: 1;

  ${media.iwMobileLandscape} {
    z-index: 2;
    right: 16px;
    top: 88px;
    left: unset;
    transform: unset;
  }
`;
